/**
 * Helpers for the numeric text inputs on the invoice forms (amounts, GST, PST).
 *
 * These are plain text inputs backed by DOUBLE PRECISION columns, so anything that
 * isn't a clean number used to travel all the way to Postgres and fail the entire
 * submission with an opaque error. Rather than silently correcting what the user
 * typed, invalid input is reported and blocks submission.
 */

// A genuinely empty value. These columns are nullable, so "nothing entered" is
// legitimate and stays distinct from a real zero.
export const isBlankNumericInput = (value) =>
    value === null || value === undefined || String(value).trim() === '';

// A negative number, correctly formed. Split out so it can be reported as its own
// mistake rather than lumped in with "not a number".
const NEGATIVE_NUMBER = /^-(\d+\.?\d*|\.\d+)$/;

/**
 * True when a field holds something safe to store: either nothing at all, or a
 * clean decimal number that is not negative. Rejects "0.5%", "$5.60", "1,234.56",
 * "1.2.3", "1-2", "abc", "-5".
 *
 * These fields are expense amounts and taxes, so a negative is always a typo.
 */
export const isValidNumericInput = (value) => {
    if (isBlankNumericInput(value)) {
        return true;
    }
    return /^(\d+\.?\d*|\.\d+)$/.test(String(value).trim());
};

/**
 * Value to send for a field that has already passed isValidNumericInput.
 * Returns a numeric string, or null when the field is empty.
 */
export const normalizeNumericInput = (value) => {
    if (isBlankNumericInput(value) || !isValidNumericInput(value)) {
        return null;
    }

    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? String(parsed) : null;
};

/**
 * Message to show for a field, or null when it is fine. Names the most common
 * mistake explicitly — users type "%" because nothing told them the field wants a
 * dollar amount.
 */
export const numericInputError = (value, label) => {
    if (isValidNumericInput(value)) {
        return null;
    }

    const raw = String(value).trim();
    if (raw.includes('%')) {
        return `${label} must be a dollar amount, not a percentage. Remove the % sign.`;
    }
    if (raw.includes('$') || raw.includes(',')) {
        return `${label} must be digits only, with no currency symbol or commas.`;
    }
    if (NEGATIVE_NUMBER.test(raw)) {
        return `${label} cannot be negative.`;
    }
    return `${label} must be a number.`;
};
